<!-- FILE: components/Table/DataTable.vue -->
<template>
  <div ref="containerRef" class="data-table-container">
    <div class="table-wrapper">
      <div class="table-content overflow-c">
        <!-- Toolbar -->
        <toolbar
          :show-weight="showWeight"
          :deliverables-data="exportDeliverables"
          :include-formulas="isLowestLevel"
          :include-children="isLowestLevel"
          :steps-fulfillment="activeStepsFulfillment"
          :total-fulfillment="activeTotalFulfillment"
          :is-root-level="isRootLevel"
          @show-weight="toggleWeight"
          @undelete="undoDelete"
          @add-row="addRowToActiveLevel"
        />

        <breadcrumb-navigation :stack="navigationStack" @navigate="handleBreadcrumbNavigate" />

        <div ref="tableRef" class="table-scroll-wrapper">
          <table class="data-table elevation-1">
            <!-- Custom Header -->
            <thead class="data-table-header">
              <!-- Progress Row -->
              <tr ref="headerRef">
                <th v-for="header in filteredHeaders" :key="`progress-${header.value}`" class="progress-cell">
                  <!-- Step Progress Bar -->
                  <custom-progress-bar
                    v-if="header.value.includes('step')"
                    color="green"
                    :height="thHeight"
                    :value="getStepBarValue(header.value)"
                    reactive
                    striped
                  >
                    {{ getStepBarLabel(header.value) }}
                  </custom-progress-bar>

                  <!-- Total Fulfillment -->
                  <custom-progress-bar
                    v-else-if="header.value === 'fulfillment'"
                    :value="activeTotalFulfillment || 0"
                    color="green"
                    :height="thHeight"
                    reactive
                    striped
                  >
                    {{
                      activeTotalFulfillment !== null && !isNaN(activeTotalFulfillment)
                        ? activeTotalFulfillment + "%"
                        : "n.rel."
                    }}
                  </custom-progress-bar>

                  <!-- Empty for other columns -->
                  <div v-else class="empty-header-cell" @click="toggleWidth">
                    &nbsp;
                  </div>
                </th>
              </tr>

              <!-- Header Titles Row -->
              <tr class="header-titles-row">
                <th
                  v-for="(header, idx) in filteredHeaders"
                  :key="`header-${idx}`"
                  class="header-cell sortable"
                  :class="{
                    active: sortBy === header.value,
                    asc: sortDesc === false,
                  }"
                >
                  <!-- Editable Headers (Steps/Responsibles) -->
                  <div class="header-content">
                    <edit-dialog-table-header
                      @handleChangeHeader="changeHeader"
                      @handleInputChange="inputChanged"
                      @handleHeaderTextFileOpend="headerTextFieldOpen"
                      :header="header"
                    />

                    <!-- Delete Icon for Editable Headers -->
                    <span
                      v-if="header.headerEditable"
                      class="delete-icon"
                      @click="conformMessage('', idx, 'column')"
                      title="Delete column"
                    >
                      <i class="icon-trash-2"></i>
                    </span>

                    <!-- Non-editable Headers -->
                    <span v-else class="non-editable-header">
                      <span @click="sortColumn(header.value)" class="header-text">
                        <span>{{ header.text }}</span>
                        <!-- <span class="sort-arrow"> -->
                        <i
                          :style="{
                            transform: `rotate(${rotateValues[header.value]}deg)`,
                            transition: 'transform 0.25s linear',
                          }"
                          @click="rotateValues[header.value] += rotateValues[header.value] === 0 ? -180 : 180"
                          aria-hidden="true"
                          class="icon-arrow-up sort-arrow"
                        ></i>
                      </span>
                    </span>
                  </div>
                </th>
              </tr>
            </thead>

            <!-- Table Body -->
            <tbody>
              <tr
                v-for="(row, idx) in sortedDeliverables"
                :key="`row-${row.node.id}`"
                ref="rowRef"
                class="table-row"
                @dblclick="handleRowDoubleClick(row.node)"
              >
                <table-cell
                  v-for="(header, key) in filteredHeaders"
                  :key="`cell-${key}`"
                  :item="row.node"
                  :header="header"
                  :expanded-width="expandedWidth"
                  :is-selected="isSelected(row.node.id)"
                  :td-height="tdHeight"
                  :indent-level="row.level"
                  :sub-project-steps="subProjectStepsFullFilment"
                  @mouseover="row.node.showEditDialog = true"
                  @mouseleave="mouseLeave(row.node)"
                  @click="handleCellClick($event, idx, row.node.id, null, header)"
                  @cell-save="handleCellSave"
                  @status-change="handleSelectChange(row.node, header.value)"
                  @handleOpen="open"
                  @handleInputChange="inputChanged"
                />
              </tr>
            </tbody>
          </table>

          <!-- Row Toolbox -->
          <div
            class="toolbox"
            v-if="toolboxVisible"
            :style="{ top: selectedRowTop + 'px' }"
            v-click-outside="closetoolbox"
          >
            <button class="toolbox-button" @click="deleteRows" title="Delete selected rows">
              <i class="icon-trash-2"></i>
            </button>
            <button class="toolbox-button" @click="createNewRowFromSelected" title="Group selected rows">
              <i class="icon-arrow-right"></i>
            </button>
            <button class="toolbox-button" @click.stop="ungroupSelectedChildren" title="Ungroup selected rows">
              <i class="icon-arrow-left"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirm Dialog -->
    <confirm-dialog
      v-if="showConformMessage"
      :message="message"
      :pressed-type="pressedType"
      @deleteRow="deleteRow"
      @deleteStep="deleteColumn('step')"
      @deleteResponsible="deleteColumn('responsible')"
      @dontDelete="toggleShowConformMessage"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import FulfilmentsUtil from "@/utils/fulfilments-util";
import ConfirmDialog from "@/components/ConfirmMessage.vue";
import CustomProgressBar from "./CustomProgressBar.vue";
import EditDialogTableHeader from "./EditDialogTableHeader.vue";
import BreadcrumbNavigation from "./BreadcrumbNavigation.vue";
import TableCell from "./TableCell.vue";
import Toolbar from "./Toolbar.vue";
import { R, asFraction, toJSON, toNumber, toPercentNumber } from "@/utils/rational";
import { STATUS_NREL, STATUS_CLOSED, STATUS_WIP } from "@/utils/excel-constants";

// Mixins
import rowOperationsMixin from "../mixin/rowOperationsMixin";
import rowSelectionMixin from "../mixin/rowSelectionMixin";
import groupingMixin from "../mixin/groupingMixin";
import columnOperationsMixin from "../mixin/columnOperationsMixin";
import cellEditingMixin from "../mixin/cellEditingMixin";

const util = new FulfilmentsUtil();

export default {
  name: "DataTable",
  components: {
    ConfirmDialog,
    CustomProgressBar,
    EditDialogTableHeader,
    BreadcrumbNavigation,
    TableCell,
    Toolbar,
  },
  mixins: [rowSelectionMixin, rowOperationsMixin, groupingMixin, columnOperationsMixin, cellEditingMixin],
  data() {
    return {
      sortBy: "id",
      sortDesc: false,
      expandedWidth: true,
      navigationStack: [],
      toolboxVisible: false,
      selectedRowTop: 0,
      selectItems: ["open", "wip", "closed", "n.rel."],
      showWeight: "Show Weight",
      thHeight: 16,
      tdHeight: 16,
      message: "",
      showConformMessage: false,
      pressedId: "",
      pressedType: "",
      rotateValues: { id: 0, description: 0, weight: 0, fulfillment: 0 },
      rules: {
        counter: (value) => value.length <= 20 || "Max 20 characters",
        required: (value) => !!value || "Required.",
        positiveNumbers: (value) => value > 0 || "Should be greater than 0",
        alreadyExist: true,
      },
      showEditDialog: true,
      file: null,
      max20chars: (v) => v.length <= 20 || "Maximum 20 character allowed",

      adjustText: "Fit On Page",
      heightScale: 0,
      widthScale: 0,
      horizontalAdjusted: false,
      thWidth: 0,
      headerEditValue: "",
      defaultItem: {
        id: "",
        description: "",
        oneWeekBefore: "",
        untilDayBefore: "",
        fulfillment: toJSON(R(0)),
      },
      stepOffset: 3,
      showDeleteIcon: false,
      columnDeleteIcon: false,
    };
  },
  computed: {
    ...mapState(["statusTable"]),
    headers() {
      return this.statusTable.headers;
    },
    filteredHeaders() {
      const values = this.headers.filter((item) => {
        if (this.showWeight === "Show Weight") {
          return item.value !== "weight";
        }
        return true;
      });
      return values;
    },

    deliverables() {
      return this.statusTable.deliverables;
    },
    displayRows() {
      const rows = [];
      const items = this.getActiveDeliverables();
      const indentLevel = this.navigationStack.length;
      if (Array.isArray(items)) {
        items.forEach((item) => {
          if (typeof item.expanded === "undefined") {
            this.$set(item, "expanded", true);
          }
          rows.push({ node: item, level: indentLevel });
        });
      }
      return rows;
    },
    // Computed property for sorted deliverables (keeps hierarchy order when sorting by id)
    sortedDeliverables() {
      const rows = this.displayRows;
      if (!this.sortBy || this.sortBy === "id") return rows;

      const sorted = [...rows].sort((a, b) => {
        const normalizeNumeric = (val, key) => {
          if (key === "weight") return toNumber(asFraction(val));
          if (key === "fulfillment") {
            if (val === "n.rel.") return -Infinity;
            if (typeof val === "number") return val;
            return toPercentNumber(val);
          }
          return val;
        };

        let aVal = normalizeNumeric(a.node[this.sortBy], this.sortBy);
        let bVal = normalizeNumeric(b.node[this.sortBy], this.sortBy);

        if (typeof aVal === "number" && typeof bVal === "number") {
          return this.sortDesc ? bVal - aVal : aVal - bVal;
        }

        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
        return this.sortDesc ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
      });

      return sorted;
    },
    responsibles() {
      return this.statusTable.responsibles;
    },
    steps() {
      return this.statusTable.steps;
    },
    stepsFulfillment() {
      return this.$store.getters.stepsFulfillment;
    },
    subProjectStepsFullFilment() {
      return this.$store.getters.subProjectStepsFullFilment;
    },
    totalFulfillment() {
      return Math.round(this.$store.getters.totalFulfillment);
    },
    activeParent() {
      return this.navigationStack.length ? this.navigationStack[this.navigationStack.length - 1] : null;
    },
    activeStepsFulfillment() {
      const base =
        this.activeParent && this.subProjectStepsFullFilment[this.activeParent.id]
          ? this.subProjectStepsFullFilment[this.activeParent.id]
          : this.stepsFulfillment;

      const scopeItems = this.getActiveDeliverables(false) || this.deliverables;
      const fallback = this.calculateStepFulfillmentForItems(scopeItems);
      return this.mergeFulfillmentMaps(base, fallback);
    },
    activeTotalFulfillment() {
      if (!this.activeParent) return this.totalFulfillment;
      const value = this.activeParent.fulfillment;
      if (value === "n.rel.") return this.totalFulfillment;
      if (typeof value === "number") return Math.round(value);
      return value ? Math.round(toPercentNumber(value)) : this.totalFulfillment;
    },
    isAuthenticated() {
      return this.$store.getters.getAuth === "signed-in";
    },
    subStatus() {
      return this.$store.getters.getSubStatus;
    },
    exportDeliverables() {
      const active = this.getActiveDeliverables(false);
      if (Array.isArray(active) && active.length > 0) {
        return active;
      }
      return this.deliverables;
    },
    isLowestLevel() {
      const items = this.exportDeliverables || [];
      return !items.some((item) => Array.isArray(item?.children) && item.children.length > 0);
    },
    isRootLevel() {
      return this.navigationStack.length === 0;
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initializeTableDimensions();
    });
  },
  methods: {
    getActiveDeliverables(createIfMissing = false) {
      const currentParent = this.ensureNavigationValid();
      if (!currentParent) {
        return this.deliverables;
      }

      if (!Array.isArray(currentParent.children)) {
        if (createIfMissing) {
          this.$set(currentParent, "children", []);
        } else {
          return [];
        }
      }
      return currentParent.children;
    },
    getVisibleRows() {
      return this.displayRows;
    },
    ensureNavigationValid() {
      while (
        this.navigationStack.length > 0 &&
        !this.isNodeInTree(this.navigationStack[this.navigationStack.length - 1], this.deliverables)
      ) {
        this.navigationStack.pop();
      }
      return this.navigationStack[this.navigationStack.length - 1] || null;
    },
    isNodeInTree(target, items) {
      const list = items || this.deliverables;
      if (!Array.isArray(list)) return false;
      for (const node of list) {
        if (node === target) return true;
        if (node && Array.isArray(node.children) && this.isNodeInTree(target, node.children)) {
          return true;
        }
      }
      return false;
    },
    handleBreadcrumbNavigate(targetIndex) {
      if (targetIndex === -1) {
        this.navigationStack = [];
      } else {
        this.navigationStack = this.navigationStack.slice(0, targetIndex + 1);
      }
      this.selectedRows = [];
      this.toolboxVisible = false;
    },
    handleRowDoubleClick(item) {
      if (!item) return;
      const hasChildren = Array.isArray(item.children) || item.isGrouped;
      if (!hasChildren) return;

      if (!Array.isArray(item.children)) {
        this.$set(item, "children", []);
      }
      this.navigationStack = [...this.navigationStack, item];
      this.selectedRows = [];
      this.toolboxVisible = false;
    },
    addRowToActiveLevel() {
      const target = this.getActiveDeliverables(true);
      if (!target) return;

      const newRow = {
        id: "",
        description: "",
        responsible: "",
        weight: toJSON(R(1)),
        fulfillment: toJSON(R(0)),
        showEditDialog: false,
        expanded: true,
      };

      this.steps.forEach((step) => {
        newRow[step.identity] = "open";
      });

      target.push(newRow);
      this.renumberDeliverables(this.deliverables);
      this.syncParentIds && this.syncParentIds(this.deliverables);
      util.recalculateAllFulfillments(this.deliverables, this.steps);
      this.$store.dispatch("setDeliverables", this.deliverables);
    },
    handleOpen(openedCell) {
      this.isDialogOpen = true;
      this.inputValue = openedCell;
    },
    initializeTableDimensions() {
      if (this.$refs.headerRef) {
        this.thHeight = this.$refs.headerRef.clientHeight || 16;
      }
      if (this.$refs.rowRef && this.$refs.rowRef[0]) {
        this.tdHeight = this.$refs.rowRef[0].clientHeight || 16;
      }

      // Set max height for scrollable area
      const rootEl = this.$refs.containerRef || this.$el;
      if (!rootEl || typeof rootEl.querySelector !== "function") return;

      const toolbar = rootEl.querySelector(".toolbar-wrapper");
      const container = rootEl.querySelector(".table-content");
      const scrollWrapper = rootEl.querySelector(".table-scroll-wrapper");
      const breadcrumb = rootEl.querySelector(".breadcrumb");

      if (toolbar && container && scrollWrapper) {
        const toolbarHeight = toolbar.clientHeight || 0;
        const breadcrumbHeight = breadcrumb ? breadcrumb.clientHeight || 0 : 0;
        const containerHeight = container.clientHeight || 0;
        if (containerHeight > 0) {
          scrollWrapper.style.maxHeight = `${containerHeight - toolbarHeight - breadcrumbHeight}px`;
        }
      }
    },

    handleCellClick(event, idx, id, parentIdx, header) {
      // Only handle clicks on ID column
      if (header.value === "id") {
        this.handleClick(event, idx, id, parentIdx);
      }
    },
    calculateStepFulfillmentForItems(items) {
      const leaves = [];
      const collectLeaves = (list) => {
        if (!Array.isArray(list)) return;
        list.forEach((item) => {
          if (item && Array.isArray(item.children) && item.children.length > 0) {
            collectLeaves(item.children);
          } else if (item) {
            leaves.push(item);
          }
        });
      };
      collectLeaves(items);

      const HALF = 0.5;
      const result = {};

      this.steps.forEach((step) => {
        let totalWeights = 0;
        let wipCount = 0;
        let closedCount = 0;

        leaves.forEach((row) => {
          const weight = toNumber(asFraction(row.weight));
          if (row[step.identity] !== STATUS_NREL) {
            totalWeights += weight;
          }
          closedCount += row[step.identity] === STATUS_CLOSED ? weight : 0;
          wipCount += row[step.identity] === STATUS_WIP ? weight * HALF : 0;
        });

        result[step.identity] = totalWeights === 0 ? STATUS_NREL : ((closedCount + wipCount) / totalWeights) * 100;
      });

      return result;
    },
    mergeFulfillmentMaps(primary, fallback) {
      const merged = {};
      this.steps.forEach((step) => {
        const key = step.identity;
        const primaryVal = primary ? primary[key] : undefined;
        const fallbackVal = fallback ? fallback[key] : undefined;
        if (primaryVal === 0 && fallbackVal === STATUS_NREL) {
          merged[key] = STATUS_NREL;
        } else if (primaryVal === undefined || primaryVal === null) {
          merged[key] = fallbackVal;
        } else {
          merged[key] = primaryVal;
        }
      });
      return merged;
    },
    getStepBarValue(stepKey) {
      const value = this.activeStepsFulfillment?.[stepKey];
      if (value === STATUS_NREL) return 0;
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : 0;
    },
    getStepBarLabel(stepKey) {
      const value = this.activeStepsFulfillment?.[stepKey];
      if (value === STATUS_NREL) return STATUS_NREL;
      const numeric = Number(value);
      return Number.isFinite(numeric) ? `${Math.round(numeric)}%` : "0%";
    },

    sortColumn(headerValue) {
      if (this.sortBy === headerValue) {
        this.sortDesc = true;

        if (this.rotateValues[headerValue] === 0) {
          this.sortDesc = false;
        }
      } else {
        this.sortDesc = false;
        this.sortBy = headerValue;
        this.rotateValues.id = 0;
        this.rotateValues.description = 0;
        this.rotateValues.weight = 0;
        this.rotateValues.fulfillment = 0;
      }
    },
    toggleWidth() {
      this.expandedWidth = !this.expandedWidth;
    },

    toggleWeight() {
      this.showWeight = this.showWeight === "Show Weight" ? "Hide Weight" : "Show Weight";
    },

    getTitle(item, header) {
      if (header.value === "weight") {
        return "";
      }
      if (header.value.includes("step") || !isNaN(item[header.value])) {
        return "";
      }
      return item[header.value];
    },

    positionToolBox(rowIdx, parentIdx) {
      const rowHeight = 40; // Adjust based on your row height
      const headerHeight = this.thHeight * 2 || 64; // Two header rows
      const positionIndex =
        parentIdx !== null && parentIdx !== undefined
          ? rowIdx + parentIdx
          : rowIdx !== null && rowIdx !== undefined
          ? rowIdx
          : 0;
      this.selectedRowTop = headerHeight + positionIndex * rowHeight;
      this.toolboxVisible = true;
    },

    closetoolbox(event) {
      if (!event.target.closest(".deliverable--number") && !event.target.closest(".toolbox")) {
        this.toolboxVisible = false;
        this.index = -1;
        this.selectedRows = [];
      }
    },

    handleCellSave(item, headerValue) {
      const normalizedValue = headerValue === "weight" ? toJSON(R(Number(this.inputValue) || 0)) : this.inputValue;
      item[headerValue] = normalizedValue;
      item.showEditDialog = false;
      this.isDialogOpen = false;
      util.recalculateAllFulfillments(this.deliverables, this.steps);
      this.$store.dispatch("setDeliverables", this.deliverables);
    },

    conformMessage(message, id, type) {
      if (type === "column") {
        if (this.showWeight === "Show Weight") {
          ++id;
        }
        if (id > 2 && id < this.responsibles.length + 3) {
          this.pressedId = id;
          this.pressedType = "responsible";
        } else {
          this.pressedId = id;
          this.pressedType = "step";
        }
        this.message = `Do you want to delete ${this.headers[id].text} column?`;
      } else {
        this.pressedId = id;
        this.pressedType = "Row";
        this.message = message;
      }
      this.showConformMessage = true;
    },

    deleteRow() {
      let id = this.pressedId;
      this.deliverables.splice(id, 1);

      for (id; id < this.deliverables.length; id++) {
        this.deliverables[id].id = this.deliverables[id].id - 1;
      }

      this.$store.dispatch("setDeliverables", this.deliverables);
      this.toggleShowConformMessage();
    },

    toggleShowConformMessage() {
      this.showConformMessage = !this.showConformMessage;
    },
  },
  directives: {
    "click-outside": {
      bind(el, binding, vnode) {
        el.clickOutsideEvent = function(event) {
          if (!(el === event.target || el.contains(event.target))) {
            vnode.context[binding.expression](event);
          }
        };
        document.body.addEventListener("click", el.clickOutsideEvent);
      },
      unbind(el) {
        document.body.removeEventListener("click", el.clickOutsideEvent);
      },
    },
  },
};
</script>

<style scoped>
/* Container Styles */
.data-table-container {
  width: 100%;
  height: 100%;
  padding: 10px 20px;
}

.table-wrapper {
  width: 100%;
  max-width: 100%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.table-content {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.overflow-c {
  overflow: auto;
}

.table-scroll-wrapper {
  overflow: auto;
  /* flex: 1; */
}

/* Table Styles */
.data-table {
  /* width: 100%; */
  border-collapse: collapse;
  background: white;
  font-size: 16px;
}

/* .elevation-1 {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
} */

/* Header Styles */
.data-table-header {
  position: sticky;
  top: 0;
  z-index: 10;
  /* background-color: #f5f5f5; */
}

.progress-cell {
  border: solid 0.5px #ddd;
  padding: 0;
  height: 24px;
}

.empty-header-cell {
  color: white;
  height: 100%;
  min-height: 16px;
  cursor: pointer;
}

.header-titles-row {
  /* background-color: #e0e0e0; */
}

.header-cell {
  padding: 8px 12px;
  font-weight: 600;
  text-align: left;
  border: 1px solid #ddd;
  position: relative;
  user-select: none;
  white-space: nowrap;
}

.header-cell.sortable {
  cursor: pointer;
}

.header-cell.sortable:hover {
  /* background-color: #d5d5d5; */
}

.header-cell.active {
  /* background-color: #c5c5c5; */
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.non-editable-header {
  display: flex;
  align-items: center;
  width: 100%;
}

.header-text {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}

.sort-arrow {
  display: inline-flex;
  align-items: center;
  color: #666;
}

/* Delete Icon */
.delete-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #666;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
}

.delete-icon:hover {
  color: #f44336;
  background-color: rgba(244, 67, 54, 0.1);
}

.delete-icon svg {
  display: block;
}

/* Body Styles */
.table-row {
  transition: background-color 0.2s;
}

.table-row:hover {
  /* background-color: #f5f5f5; */
}

/* .child-row {
  background-color: #fafafa;
} */

/* .child-row:hover {
  background-color: #f0f0f0;
} */

/* Toolbox Styles */

.toolbox {
  position: absolute;
  display: flex;
  flex-direction: column;
  border-radius: 18px;
  background: rgba(0, 0, 0, 0.7);
  padding: 7px 1px;
  z-index: 10;
}

.toolbox i,
.toolbox button {
  font-size: 16px;
  color: white !important;
  /* background: #4CAF50; */
  border-radius: 50%;
  padding: 2px;
  margin-bottom: 4px;
  /* opacity: 0.4; */
}

/* .toolbox {
  position: fixed;
  right: 20px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  gap: 8px;
  z-index: 1000;
}

.toolbox-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 4px;
  color: #666;
  transition: all 0.2s;
} */

.toolbox-button:hover {
  /* background-color: #f5f5f5; */
  color: #1976d2;
}

.toolbox-button:active {
  transform: scale(0.95);
}

.toolbox-button svg {
  display: block;
}

/* Utility Classes */
.selected-row {
  background-color: #e3f2fd !important;
}

/* Responsive */
@media (max-width: 768px) {
  .data-table {
    font-size: 12px;
  }

  .header-cell {
    padding: 6px 8px;
  }

  .toolbox {
    right: 10px;
  }
}
</style>
